/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */
import React, { type ReactElement, useMemo, useCallback, memo } from 'react';
import type { EntryPointRouteParams } from '@jira/platform__entry-points-plugin-types/src/types.tsx';
import {
	ExpandableMenuItemContent,
	ExpandableMenuItemTrigger,
} from '@atlaskit/side-nav-items/expandable-menu-item';
// eslint-disable-next-line jira/restricted/@atlassian+jira-entry-point
// eslint-disable-next-line jira/restricted/@atlassian+jira-entry-point
// eslint-disable-next-line jira/restricted/@atlassian+jira-entry-point
import { useEntryPoint } from '@jira/platform__entry-point/src/controllers/use-entry-point/index.tsx';
import { SECTION_ITEM_DASHBOARDS } from '@jira/navigation-apps__sidebar-nav4-analytics/src/common/constants/analytics/section-type.tsx';
import { MENU_ID_DASHBOARDS } from '@jira/navigation-apps__sidebar-nav4-context/src/common/constants.tsx';
import {
	L1_MENU_ID,
	type LeftSidebarEntryPointConfig,
	type Nav4DragAndDropMenuItemProps,
} from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/types.tsx';
import { Nav4ExpandableMenuItem } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/ui/nav4-expandable-menu-item/index.tsx';
import { SkeletonNavMenuItems } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/ui/skeleton-nav-menu-items/main.tsx';
import { getTestId } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/utils/get-test-id/index.tsx';
import { useSectionItemAnalytics } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/utils/use-section-item-analytics/index.tsx';
import { useConfigurableMessageAndIconMap } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/controllers/use-configurable-message-and-icon-map/index.tsx';
import { useExpandableEntryPointTrigger } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/controllers/use-expandable-entry-point-trigger/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { LEFT_SIDEBAR } from '@jira/platform__entry-points-page-layout-fields/src/index.tsx';
import { usePageLayoutEntryPoint } from '@jira/platform__entry-points-page-layout-entry-point/src/index.tsx';
import { RouteEntryPointContainer } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/ui/route-entry-point-adapter/index.tsx';
import { useCurrentUser } from '@jira/platform__services-user-current/src/main.tsx';
import { useActivationId } from '@atlassian/jira-tenant-context-controller/src/components/activation-id/index.tsx';
import { mergeRefs } from '@atlassian/jira-merge-refs';
// eslint-disable-next-line jira/import/file-extension-required
import { componentWithFG } from '@jira/platform__utils-feature-gates-gate-component';
import { PACKAGE_NAME, TEAM_NAME } from '../../../common/constants.tsx';
import { Footer } from '../../../common/ui/footer/index.tsx';
import { nav4DashboardsContentEntryPoint as entryPoint } from './async-content/nav4DashboardsContentEntryPoint.entrypoint.tsx';

export type DashboardsDefaultProps = Nav4DragAndDropMenuItemProps & {
	actionsOnHover: ReactElement;
	isExpanded: boolean;
};

const DashboardsDefaultInner = componentWithFG(
	'navx-global-side-nav',
	DashboardsDefaultInnerNEW,
	DashboardsDefaultInnerOLD,
);

export const DashboardsDefault: React.NamedExoticComponent<
	Nav4DragAndDropMenuItemProps & {
		actionsOnHover: ReactElement;
		isExpanded: boolean;
	}
> = memo(DashboardsDefaultInner);

function DashboardsDefaultInnerNEW(props: DashboardsDefaultProps) {
	const {
		actionsOnHover,
		draggableRef,
		dropIndicator,
		hasDragIndicator,
		isDragging,
		isExpanded,
		onExpandFlyoutToggle,
		visualContentRef,
	} = props;
	const cloudId = useCloudId();
	const {
		data: {
			user: { accountId },
		},
	} = useCurrentUser();
	const activationId = useActivationId();

	// Ignore this error as our component does not need the context.
	// @ts-expect-error - Property 'context' is missing in type '{ tenantContext: { cloudId: string; }; }' but required in type 'EntryPointRouteParams'.ts(2741)
	const entryPointParams: EntryPointRouteParams = useMemo(
		() => ({ tenantContext: { cloudId, atlassianAccountId: accountId, activationId } }),
		[cloudId, accountId, activationId],
	);

	// Suppressed due to new lint violation when upgrading to eslint-plugin-react-hooks v5
	// oxlint-disable-next-line typescript/consistent-type-assertions
	const leftSidebarEPRef = usePageLayoutEntryPoint(LEFT_SIDEBAR) as LeftSidebarEntryPointConfig;
	const dashboardsEPRef = leftSidebarEPRef?.entryPoints?.dashboards;

	const { entryPointActions, entryPointReferenceSubject } = useEntryPoint(
		entryPoint,
		entryPointParams,
	);

	const triggerRef = useExpandableEntryPointTrigger(entryPointActions);
	const { formattedMessage, icon } = useConfigurableMessageAndIconMap(L1_MENU_ID.DASHBOARDS);
	const sendSectionItemAnalyticsEvent = useSectionItemAnalytics();

	const epContainerProps = useMemo(
		() => ({
			id: 'nav4-sidebar-dashboards-default',
			packageName: PACKAGE_NAME,
			teamName: TEAM_NAME,
			errorFallback: 'flag' as const,
			fallback: <SkeletonNavMenuItems withHeading />,
			// oxlint-disable-next-line typescript/consistent-type-assertions
			runtimeProps: entryPointParams as never,
		}),
		[entryPointParams],
	);

	// This callback makes sure that the EP is loaded when the _system_ determines that the expandable
	// should be open.
	const handleSystemToggle = useCallback(
		(hasExpanded: boolean) => {
			if (hasExpanded) {
				entryPointActions.load();
			}
		},
		[entryPointActions],
	);

	const handleExpansionToggle = useCallback(
		(hasExpanded: boolean) => {
			if (hasExpanded) {
				entryPointActions.load();
				sendSectionItemAnalyticsEvent({
					menuId: L1_MENU_ID.DASHBOARDS,
					sectionItem: SECTION_ITEM_DASHBOARDS,
				});
			}
			onExpandFlyoutToggle?.(hasExpanded);
		},
		[entryPointActions, onExpandFlyoutToggle, sendSectionItemAnalyticsEvent],
	);

	const menuId = MENU_ID_DASHBOARDS;

	return (
		<Nav4ExpandableMenuItem
			dropTargetRef={visualContentRef}
			dropIndicator={dropIndicator}
			isDragging={isDragging}
			isExpanded={isExpanded}
			menuId={menuId}
			onSystemToggle={handleSystemToggle}
			onExpansionToggle={handleExpansionToggle}
		>
			<ExpandableMenuItemTrigger
				interactionName="nav4-sidebar-dashboards-default"
				actionsOnHover={actionsOnHover}
				aria-label={formattedMessage}
				elemBefore={icon}
				hasDragIndicator={hasDragIndicator}
				isDragging={isDragging}
				ref={draggableRef ? mergeRefs(triggerRef, draggableRef) : triggerRef}
				testId={getTestId(menuId)}
			>
				{formattedMessage}
			</ExpandableMenuItemTrigger>
			<ExpandableMenuItemContent>
				<RouteEntryPointContainer
					epContainerProps={epContainerProps}
					jiraEPRef={entryPointReferenceSubject}
					nestedEPRef={dashboardsEPRef}
					placeholderEntryPoint={entryPoint}
				/>
				<Footer />
			</ExpandableMenuItemContent>
		</Nav4ExpandableMenuItem>
	);
}

function DashboardsDefaultInnerOLD(props: DashboardsDefaultProps) {
	const { actionsOnHover, isExpanded } = props;
	const cloudId = useCloudId();
	const {
		data: {
			user: { accountId },
		},
	} = useCurrentUser();
	const activationId = useActivationId();

	// Ignore this error as our component does not need the context.
	// @ts-expect-error - Property 'context' is missing in type '{ tenantContext: { cloudId: string; }; }' but required in type 'EntryPointRouteParams'.ts(2741)
	const entryPointParams: EntryPointRouteParams = useMemo(
		() => ({ tenantContext: { cloudId, atlassianAccountId: accountId, activationId } }),
		[cloudId, accountId, activationId],
	);

	// Suppressed due to new lint violation when upgrading to eslint-plugin-react-hooks v5
	// oxlint-disable-next-line typescript/consistent-type-assertions
	const leftSidebarEPRef = usePageLayoutEntryPoint(LEFT_SIDEBAR) as LeftSidebarEntryPointConfig;
	const dashboardsEPRef = leftSidebarEPRef?.entryPoints?.dashboards;

	const { entryPointActions, entryPointReferenceSubject } = useEntryPoint(
		entryPoint,
		entryPointParams,
	);

	const triggerRef = useExpandableEntryPointTrigger(entryPointActions);
	const { formattedMessage, icon } = useConfigurableMessageAndIconMap(L1_MENU_ID.DASHBOARDS);
	const sendSectionItemAnalyticsEvent = useSectionItemAnalytics();

	const epContainerProps = useMemo(
		() => ({
			id: 'nav4-sidebar-dashboards-default',
			packageName: PACKAGE_NAME,
			teamName: TEAM_NAME,
			errorFallback: 'flag' as const,
			fallback: <SkeletonNavMenuItems withHeading />,
			// oxlint-disable-next-line typescript/consistent-type-assertions
			runtimeProps: entryPointParams as never,
		}),
		[entryPointParams],
	);

	// This callback makes sure that the EP is loaded when the _system_ determines that the expandable
	// should be open.
	const onSystemToggleFn = useCallback(
		(hasExpanded: boolean) => {
			if (hasExpanded) {
				entryPointActions.load();
			}
		},
		[entryPointActions],
	);

	const onExpansionToggleFn = useCallback(
		(hasExpanded: boolean) => {
			if (hasExpanded) {
				entryPointActions.load();
				sendSectionItemAnalyticsEvent({
					menuId: L1_MENU_ID.DASHBOARDS,
					sectionItem: SECTION_ITEM_DASHBOARDS,
				});
			}
		},
		[entryPointActions, sendSectionItemAnalyticsEvent],
	);

	const menuId = MENU_ID_DASHBOARDS;

	return (
		<Nav4ExpandableMenuItem
			isExpanded={isExpanded}
			menuId={menuId}
			onSystemToggle={onSystemToggleFn}
			onExpansionToggle={onExpansionToggleFn}
		>
			<ExpandableMenuItemTrigger
				interactionName="nav4-sidebar-dashboards-default"
				actionsOnHover={actionsOnHover}
				aria-label={formattedMessage}
				elemBefore={icon}
				ref={triggerRef}
				testId={getTestId(menuId)}
			>
				{formattedMessage}
			</ExpandableMenuItemTrigger>
			<ExpandableMenuItemContent>
				<RouteEntryPointContainer
					epContainerProps={epContainerProps}
					jiraEPRef={entryPointReferenceSubject}
					nestedEPRef={dashboardsEPRef}
					placeholderEntryPoint={entryPoint}
				/>
				<Footer />
			</ExpandableMenuItemContent>
		</Nav4ExpandableMenuItem>
	);
}
